// Copyright ©️ 2026 eVolve MEP, LLC

import type { SavedGridStateJsonString } from '@evolvemep/api-ts';
import type { ColumnSizeState, GridApi } from 'ag-grid-enterprise';

import { parseGridStateJson } from 'helpers/ag-grid/TableViewPanel/utils';
import { isNil } from 'helpers/isNotNil';

export const loadSavedGridState = (gridState: SavedGridStateJsonString, gridApi: GridApi, refreshData = false) => {
  const columnDefs = gridApi.getColumnDefs();
  const leftLockedColumnIds: string[] = [];
  const rightLockedColumnIds: string[] = [];
  const leftLockedAndPinnedColumnIds: string[] = [];
  const rightLockedAndPinnedColumnIds: string[] = [];
  const hiddenLockedColumnIds: string[] = [];
  const lockedColumnSizingModel: ColumnSizeState[] = [];

  // Process column definitions to extract locked column info.
  columnDefs?.forEach((colDef) => {
    if (!('lockPosition' in colDef) || isNil(colDef.colId)) return;

    // Column order and pinning
    if (colDef.lockPosition === 'left') {
      leftLockedColumnIds.push(colDef.colId);
      if (colDef.pinned === 'left') {
        leftLockedAndPinnedColumnIds.push(colDef.colId);
      }
    } else if (colDef.lockPosition === 'right') {
      rightLockedColumnIds.push(colDef.colId);
      if (colDef.pinned === 'right') {
        rightLockedAndPinnedColumnIds.push(colDef.colId);
      }
    }

    // Column sizing
    lockedColumnSizingModel.push({ colId: colDef.colId, flex: colDef.flex ?? undefined, width: colDef.width });

    // Column visibility
    if (colDef.hide) hiddenLockedColumnIds.push(colDef.colId);
  });

  // Merge locked column config from column definitions with the saved grid state.
  const mergedGridState = parseGridStateJson(gridState);
  if (isNil(mergedGridState)) return;

  if (mergedGridState.columnOrder) {
    mergedGridState.columnOrder = {
      ...mergedGridState.columnOrder,
      orderedColIds: [...leftLockedColumnIds, ...mergedGridState.columnOrder.orderedColIds, ...rightLockedColumnIds],
    };
  }
  if (mergedGridState.columnPinning) {
    mergedGridState.columnPinning = {
      ...mergedGridState.columnPinning,
      leftColIds: [...leftLockedAndPinnedColumnIds, ...mergedGridState.columnPinning.leftColIds],
      rightColIds: [...mergedGridState.columnPinning.rightColIds, ...rightLockedAndPinnedColumnIds],
    };
  }
  if (mergedGridState.columnSizing) {
    mergedGridState.columnSizing = {
      ...mergedGridState.columnSizing,
      columnSizingModel: [...lockedColumnSizingModel, ...mergedGridState.columnSizing.columnSizingModel],
    };
  }
  if (mergedGridState.columnVisibility) {
    mergedGridState.columnVisibility = {
      ...mergedGridState.columnVisibility,
      hiddenColIds: [...mergedGridState.columnVisibility.hiddenColIds, ...hiddenLockedColumnIds],
    };
  }

  gridApi.setState(mergedGridState, ['sideBar']);

  // Refresh any server-side data to reflect the new filters and sorts.
  if (!refreshData || gridApi.getGridOption('rowModelType') !== 'serverSide') return;
  gridApi.refreshServerSide({ purge: true });
};
